import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../../lib/adminApi';
import { toast } from 'sonner';
import {
  MagnifyingGlass,
  Prohibit,
  CheckCircle,
  Link as LinkIcon,
  Warning,
  ArrowClockwise,
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';

const PAGE_LIMIT = 20;

export default function AdminLinks() {
  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const debounceRef = useRef(null);

  const fetchLinks = useCallback(async (currentPage, currentSearch) => {
    try {
      setLoading(true);
      setError(false);
      const params = { page: currentPage, limit: PAGE_LIMIT };
      if (currentSearch) params.search = currentSearch;
      const res = await adminApi.getLinks(params);
      setLinks(res.items || []);
      setTotal(res.total || 0);
    } catch (err) {
      console.error('Failed to load links', err);
      setError(true);
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks(page, search);
  }, [fetchLinks, page, search]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 300);
  };

  const handleToggleDisable = async (link) => {
    const action = link.status === 'disabled' ? 'enable' : 'disable';
    try {
      setActionLoading(link.link_id);
      if (link.status === 'disabled') {
        await adminApi.enableLink(link.link_id);
      } else {
        await adminApi.disableLink(link.link_id);
      }
      toast.success(`Link ${action}d successfully`);
      fetchLinks(page, search);
    } catch (err) {
      toast.error(err.message || `Failed to ${action} link`);
    } finally {
      setActionLoading(null);
    }
  };

  const StatusBadge = ({ status }) => {
    if (status === 'active') {
      return (
        <span className="chip bg-emerald-50 text-emerald-700">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
          active
        </span>
      );
    }
    if (status === 'disabled') {
      return (
        <span className="chip bg-rose-50 text-rose-600">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
          disabled
        </span>
      );
    }
    return (
      <span className="chip bg-muted text-muted-foreground">
        {status || 'unknown'}
      </span>
    );
  };

  const totalPages = Math.ceil(total / PAGE_LIMIT);

  if (loading && links.length === 0) {
    return (
      <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full">
        <div className="h-9 w-40 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="h-12 w-80 bg-muted rounded-xl animate-pulse mb-6" />
        <div className="surface-card overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-16 border-t border-border/70 animate-pulse first:border-t-0" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-10 animate-fade-in" data-testid="admin-links-error">
        <div className="surface-card p-12 flex flex-col items-center justify-center text-center max-w-2xl mx-auto">
          <div className="grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive mb-4">
            <Warning size={26} weight="fill" />
          </div>
          <h2 className="font-display text-2xl font-extrabold text-foreground mb-2">
            Failed to load links
          </h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-md">
            We couldn't fetch the link list. Check your connection and try again.
          </p>
          <Button
            onClick={() => fetchLinks(page, search)}
            data-testid="retry-btn"
            className="rounded-xl gradient-primary text-white hover:opacity-95 px-6 py-2.5 font-semibold gap-2"
          >
            <ArrowClockwise size={16} weight="bold" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 animate-fade-in max-w-[1280px] mx-auto w-full" data-testid="admin-links-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-[2.5rem] font-extrabold tracking-tight text-foreground leading-tight">
            Links
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            <span className="font-medium text-foreground/90">{total}</span> total link
            {total !== 1 ? 's' : ''} across the platform.
          </p>
        </div>
        <div className="relative w-full sm:w-80">
          <MagnifyingGlass
            size={16}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <input
            type="search"
            placeholder="Search by slug, URL or title…"
            onChange={handleSearchChange}
            className="input-modern pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      <div className="surface-card overflow-hidden ambient-shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-modern">
            <thead>
              <tr>
                <th className="text-left">Link</th>
                <th className="text-left">Workspace</th>
                <th className="text-right">Clicks</th>
                <th className="text-left">Status</th>
                <th className="text-left">Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {links.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center text-muted-foreground">
                      <LinkIcon size={32} weight="duotone" className="mb-2 text-muted-foreground/70" />
                      <span className="text-sm">
                        {search ? 'No links match your search.' : 'No links found.'}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                links.map((link) => (
                  <tr key={link.link_id} className="link-row">
                    <td>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary shrink-0">
                          <LinkIcon size={14} weight="fill" />
                        </span>
                        <div className="min-w-0">
                          <p className="font-mono text-xs font-semibold text-foreground truncate">
                            /{link.slug || '—'}
                          </p>
                          <p
                            className="text-xs text-muted-foreground truncate max-w-[260px]"
                            title={link.destination_url}
                          >
                            {link.destination_url || '—'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="text-muted-foreground truncate max-w-[180px]">
                      {link.workspace_name || '—'}
                    </td>
                    <td className="text-right font-semibold text-foreground tabular-nums">
                      {(link.total_clicks ?? 0).toLocaleString()}
                    </td>
                    <td>
                      <StatusBadge status={link.status} />
                    </td>
                    <td className="text-muted-foreground">
                      {link.created_at
                        ? new Date(link.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleDisable(link)}
                        disabled={actionLoading === link.link_id}
                        className="h-8 w-8 p-0 rounded-lg hover:bg-muted"
                        title={link.status === 'disabled' ? 'Enable link' : 'Disable link'}
                        data-testid={`toggle-disable-${link.link_id}`}
                      >
                        {link.status === 'disabled' ? (
                          <CheckCircle size={16} className="text-emerald-600" />
                        ) : (
                          <Prohibit size={16} className="text-amber-600" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/70 bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Page <span className="font-semibold text-foreground">{page}</span> of {totalPages}
              <span className="text-muted-foreground/70"> · {total} links</span>
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="rounded-lg text-xs h-8 px-3"
                data-testid="next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
